import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { ChatMessage, ChatStatus, Citation } from "@/lib/types";

interface MessageRow {
  id: string;
  conversation_id: string;
  role: "user" | "assistant";
  content: string;
  dify_message_id: string | null;
  citations: Citation[] | null;
  source: string | null;
  status: string | null;
  created_at: string;
}

function mapRow(r: MessageRow): ChatMessage {
  return {
    id: r.id,
    role: r.role,
    content: r.content,
    citations: r.citations ?? undefined,
    source: r.source ?? undefined,
    status: (r.status as ChatStatus | null) ?? undefined,
    messageId: r.dify_message_id ?? undefined,
    createdAt: new Date(r.created_at).getTime(),
  };
}

export function messagesKey(conversationId: string | null | undefined) {
  return ["messages", conversationId ?? "none"] as const;
}

export function useMessages(conversationId: string | null | undefined) {
  const qc = useQueryClient();
  const enabled = !!conversationId;

  const query = useQuery({
    queryKey: messagesKey(conversationId),
    enabled,
    queryFn: async (): Promise<ChatMessage[]> => {
      const { data, error } = await supabase
        .from("messages")
        .select("id, conversation_id, role, content, dify_message_id, citations, source, status, created_at")
        .eq("conversation_id", conversationId!)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return ((data as unknown as MessageRow[] | null) ?? []).map(mapRow);
    },
  });

  const insertUserMessage = useCallback(
    async (convId: string, content: string): Promise<ChatMessage> => {
      const { data, error } = await supabase
        .from("messages")
        .insert({ conversation_id: convId, role: "user", content })
        .select("id, conversation_id, role, content, dify_message_id, citations, source, status, created_at")
        .single();
      if (error) throw error;
      return mapRow(data as unknown as MessageRow);
    },
    [],
  );

  const insertAssistantMessage = useCallback(
    async (
      convId: string,
      msg: {
        content: string;
        status?: string | null;
        source?: string | null;
        citations?: Citation[] | null;
        dify_message_id?: string | null;
        /** Varianti fra cui scegliere. Non persistite: valgono per questa risposta. */
        suggestions?: string[];
      },
    ): Promise<ChatMessage> => {
      const { data, error } = await supabase
        .from("messages")
        .insert({
          conversation_id: convId,
          role: "assistant",
          content: msg.content,
          status: msg.status ?? null,
          source: msg.source ?? null,
          citations: (msg.citations ?? null) as unknown as never,
          dify_message_id: msg.dify_message_id ?? null,
        })
        .select("id, conversation_id, role, content, dify_message_id, citations, source, status, created_at")
        .single();
      if (error) throw error;
      const riga = mapRow(data as unknown as MessageRow);
      // I chip di disambiguazione servono solo qui e ora: la tabella `messages`
      // non ha la colonna, e riproporli rileggendo lo storico non avrebbe senso
      // (la scelta e' gia' stata fatta).
      return msg.suggestions?.length ? { ...riga, suggestions: msg.suggestions } : riga;
    },
    [],
  );

  const invalidate = useCallback(
    (convId: string) => {
      qc.invalidateQueries({ queryKey: messagesKey(convId) });
    },
    [qc],
  );

  return {
    messages: query.data ?? [],
    isLoading: query.isLoading,
    insertUserMessage,
    insertAssistantMessage,
    invalidate,
  };
}